import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import {
  Checkbox,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  useTheme,
  InputAdornment,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import RuleIcon from '@mui/icons-material/Rule';
import ReplayIcon from '@mui/icons-material/Replay';
import FieldModal from '../fieldModal/FieldModal';
import FormatClear from '@mui/icons-material/FormatClear';
import { useAtom, useAtomValue } from 'jotai';
import { validationDismissalDialogAtom } from '../../../atoms/spreadsheet-atoms';
import { markShowValidationsAtom } from '../../../atoms/mark-validation-atoms';
import errorResponses from '../../../errorResponses';
import useGlynt from '../../hooks/useGlynt';
import { AppContext } from '../../../App';
import { FIELD_CONTENT_LIMIT } from '../../../constants';
import { FIELD_DATA_TYPE_CONFIG_DEFAULT } from '../../fields/FieldsSettings/FieldsSettingsTypes';
import { DivInputWrapper, DivInputLabelStyled } from '../fieldModal/FieldStyles';
import { IToken } from '../../../types/my-types';
import { Inexpressible } from '../../../types/util';
import { MarkDispatchContext, MarkStateContext } from '../../../context/MarkContext';
import { MarkFieldProps } from './MarkFieldTypes';
import {
  colorMap,
  isRequiredAndValueEmpty,
  whichColorMapObject,
  getHighestPriorityValidationError,
  getValidationMessages,
} from '../../../utils/validationUtils';
import { featureFlags } from '../../../utils/featureFlags';
import { getConfigByDataType } from '../../../utils/getConfigByDataType';
import { getDataFormat } from '../../../utils/getDataFormat';
import { getDataTypeOptionsFromQuery } from '../../../utils/getDataTypeOptionsFromQuery';
import { getDefaultDataFormat } from '../../../utils/getDefaultDataFormat';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';

import { getNextActiveField } from '../../../utils/getNextActiveField';
import FieldValidationIcons from '../../fields/FieldsSettings/FieldValidationsIcons';
import {
  useExtractionValidationState,
  VALIDATING_TOOLTIP,
  VALIDATION_NOT_READY_TOOLTIP,
} from '../../hooks/useValidationFetchState';

import MarkFieldStyles from './MarkFieldStyles.module.scss';

interface MarkFieldStateProps {
  focused: boolean;
  tokens: IToken[];
  isSaving: boolean;
  hasMismatchedTokens: boolean;
  isSpreadsheetFocused: boolean;
  showValidationsContext: boolean;
  selectedFields: string[];
  showFieldCheckboxes: boolean;
  accessibility: boolean;
  dataPoolId: string;
}

const NO_TOKENS: IToken[] = [];
const NO_SELECTED_FIELDS: string[] = [];

/**
 * Field input for the mark and review tools.
 *
 * Reads the shared mark state and hands the field view only the slice that concerns
 * this field, so a step on another field (focus, token selection) does not re-render
 * every row of a long field list.
 */
const MarkField = (props: MarkFieldProps) => {
  const {
    activeField,
    tokens,
    isSaving,
    misMatchedIndexTokens,
    spreadsheetFocusedCell,
    showValidations,
    selectedFields,
    showFieldCheckboxes,
    fieldListFocused,
  } = useContext(MarkStateContext);
  const { accessibility, dataPoolId } = useContext(AppContext);
  const focused = activeField === props.field && fieldListFocused;

  return (
    <MarkFieldView
      {...props}
      focused={focused}
      tokens={focused ? tokens : NO_TOKENS}
      isSaving={isSaving}
      hasMismatchedTokens={misMatchedIndexTokens.some((t) => t.field_label === props.field)}
      isSpreadsheetFocused={spreadsheetFocusedCell?.colId === props.field}
      showValidationsContext={showValidations}
      selectedFields={showFieldCheckboxes ? selectedFields : NO_SELECTED_FIELDS}
      showFieldCheckboxes={showFieldCheckboxes}
      accessibility={accessibility}
      dataPoolId={dataPoolId}
    />
  );
};

const MarkFieldView = memo(function MarkFieldView(props: MarkFieldProps & MarkFieldStateProps) {
  const {
    ebId,
    tsId,
    extractionId,
    field,
    fields,
    fieldId,
    fieldsCount,
    fieldDataType,
    fieldDataTypeConfig,
    glyntTags,
    index,
    pageIndex,
    initValue,
    isMutationError,
    isMutationLoading,
    isReview,
    save,
    validationErrorsForThisField,
    highlightField,
    isAdditionalField,
    validationConfig,
    readOnly = false,
    focused,
    tokens,
    isSaving,
    hasMismatchedTokens,
    isSpreadsheetFocused,
    showValidationsContext,
    selectedFields,
    showFieldCheckboxes,
    accessibility,
    dataPoolId,
  } = props;

  const { dpid, id, eid, did } = useParams<{
    dpid: string;
    id: string;
    eid: string;
    did: string;
  }>();

  const glynt = useGlynt();
  const theme = useTheme();
  const dispatch = useContext<React.Dispatch<any>>(MarkDispatchContext);
  const markShowValidations = useAtomValue(markShowValidationsAtom);
  // Review only: while the extraction's validation is being refetched (or its poll budget
  // ran out) the label shows that state instead of the last validation result.
  const { fetchState: validationFetchState, retry: retryValidation } = useExtractionValidationState(
    isReview ? extractionId : undefined
  );
  const isValidating = validationFetchState === 'validating';
  const isValidationNotReady = validationFetchState === 'not-ready';

  // Use the appropriate showValidations value based on whether we're in review or mark mode
  const showValidations = isReview ? showValidationsContext : markShowValidations;

  const [value, setValue] = useState(initValue);
  const valueRef = useRef(value);
  valueRef.current = value;
  const [errorMessage, setErrorMessage] = useState('');
  const [updateFieldListLabel, setUpdateFieldListLabel] = useState(false);
  // Track if user has manually edited the field - prevents resetting to initValue on blur
  const userEditedRef = useRef(false);

  const [openModal, setOpenModal] = useState(false);
  const [, setValidationDismissalDialog] = useAtom(validationDismissalDialogAtom);

  const ref = useRef(null);

  const dtQuery = useQuery({
    queryKey: ['dataTypes'],
    queryFn: async () => await (await glynt).dataTypes.retrieve(),
    enabled: !!dataPoolId,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60,
  });

  const dataTypesResponse = dtQuery?.data;

  useEffect(() => {
    if (focused) {
      (ref.current as Inexpressible).focus();
      dispatch({
        type: 'SET_SPREADSHEET_FOCUSED_CELL',
        payload: {
          rowId: eid,
          colId: field,
        },
      });
      dispatch({ type: 'FOCUS_FIELD', payload: { field, pageIndex } });
    }
  }, [focused, dispatch, eid, field, pageIndex]);

  useEffect(() => {
    if (value.length >= FIELD_CONTENT_LIMIT) {
      setErrorMessage(`Field content too long. Must be less than ${FIELD_CONTENT_LIMIT}`);
    } else {
      setErrorMessage('');
    }
  }, [value]);

  // Update input box value when tokens are selected
  useEffect(() => {
    if (isMutationLoading || isSaving || !focused) return;

    if (tokens.length > 0) {
      // Deep copy tokens for sorting
      const myTokens: IToken[] = [...tokens];
      const newValue = myTokens
        .sort((a: IToken, b: IToken) => a.bbox[0].x - b.bbox[0].x)
        .map((t: IToken) => t.content)
        .join(' ');
      setValue(newValue);
    }
  }, [tokens, focused, isMutationLoading, isSaving]);

  // Update input box value when the extraction or document changes
  useEffect(() => {
    if (isMutationLoading || isSaving) return;
    // Don't overwrite user's in-progress edits from background refetches
    if (focused && userEditedRef.current) return;
    setValue(initValue);
    userEditedRef.current = false;
  }, [eid, did, initValue, isMutationLoading, isSaving]);

  // Update input box value when field list label updates or initValue changes
  useEffect(() => {
    if (isMutationLoading || isSaving) return;

    if (updateFieldListLabel) {
      setValue(initValue);
      setUpdateFieldListLabel(false);
      userEditedRef.current = false;
    } else if (!focused && initValue !== value) {
      // Revert to initValue when unfocused — unsaved edits should not persist
      setValue(initValue);
      userEditedRef.current = false;
    }
  }, [updateFieldListLabel, initValue, value, focused, isMutationLoading, isSaving]);

  const clear = async () => {
    (ref.current as Inexpressible).focus();
    setValue('');
    setErrorMessage('');
    dispatch({ type: 'RESET_TOKENS' });
    userEditedRef.current = true; // Mark as edited so clear value persists
  };

  const handleSave = async () => {
    if (errorMessage.length === 0) {
      await save(valueRef.current, tokens);
      dispatch({ type: 'SAVE_FIELD' });
      userEditedRef.current = false;
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event?.preventDefault();
    dispatch({ type: 'FOCUS_FIELD', payload: { field, pageIndex, fieldListFocused: true } });
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (errorMessage.length === 0) {
        await save(valueRef.current, tokens);
        userEditedRef.current = false;

        const nextField = getNextActiveField(fields, field);
        dispatch({
          type: 'SAVE_FIELD',
          payload: { nextActiveField: nextField, fieldsCount, fieldListFocused: true },
        });
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValue(event.target.value);
    userEditedRef.current = true;
  };

  const handleModal = () => {
    setOpenModal(true);
  };

  const handleCheckboxChecked = () => {
    return isReview ? selectedFields?.includes(field) : selectedFields?.includes(fieldId);
  };

  const handleCheckboxOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    const fieldValue = isReview ? field : fieldId;

    dispatch({
      type: 'SET_SELECTED_FIELDS',
      payload: isChecked
        ? [...selectedFields, fieldValue]
        : selectedFields.filter((f) => f !== fieldValue),
    });

    dispatch({
      type: isChecked ? 'SET_SELECTED_FIELDS_INFO' : 'REMOVE_SELECTED_FIELD_INFO',
      payload: isChecked
        ? [
            {
              id: fieldId,
              label: field,
              data_type: fieldDataType,
              data_type_config: fieldDataTypeConfig,
              glynt_tags: glyntTags,
              content: initValue,
            },
          ]
        : [fieldValue],
      isReview,
    });
  };

  const showTooltip = () => {
    let config = {};

    if (fieldDataType) {
      // Use the field's config if available, otherwise fall back to default
      if (fieldDataTypeConfig) {
        config = getConfigByDataType(fieldDataType, fieldDataTypeConfig);
      } else {
        const defaultConfig = FIELD_DATA_TYPE_CONFIG_DEFAULT[fieldDataType];
        const transformedData = getDataTypeOptionsFromQuery(dataTypesResponse, fieldDataType);
        const defaultFormat = getDefaultDataFormat(fieldDataType, defaultConfig, transformedData);
        config = getConfigByDataType(fieldDataType, defaultFormat);
      }
    }

    const uiLabel = getDataFormat(fieldDataType, config);

    if (fieldDataType) {
      return fieldDataType === 'string'
        ? 'String Type'
        : `${fieldDataType.charAt(0).toUpperCase() + fieldDataType.slice(1)} Type: ${uiLabel}`;
    }

    return '';
  };

  // Retrieve a list of all validation errors (for tooltip)
  const allValidationErrorsMessages: string[] = getValidationMessages(
    validationErrorsForThisField ?? [],
    initValue
  );

  // Get the highest priority validation error
  const highestPriorityValidationError = getHighestPriorityValidationError(
    validationErrorsForThisField ?? []
  );

  // Check if field error is dismissed
  const fieldErrorIsDismissed = validationErrorsForThisField?.some(
    (error) => error.dismissalReason
  );

  const hasStaleValidationResult = isValidating || isValidationNotReady;
  const fieldHasValidationError =
    allValidationErrorsMessages.length > 0 && !hasStaleValidationResult;
  const fieldHasNonDismissedError = fieldHasValidationError && !fieldErrorIsDismissed;

  // Buttons should be visible always; disable when interaction isn't allowed
  const disableClear = readOnly || !focused || isMutationLoading || isSaving;
  const disableSave = disableClear || errorMessage.length > 0;

  const handleOpenDismissalDialog = () => {
    if (!dpid || !eid || !highestPriorityValidationError) {
      return;
    }

    setValidationDismissalDialog({
      isOpen: true,
      dataPoolId: dpid,
      validationId: highestPriorityValidationError.id,
      extractionId: eid,
      field,
      currentDismissalReason: highestPriorityValidationError.dismissalReason || null,
      error: highestPriorityValidationError.error || null,
      errorDetail: highestPriorityValidationError.errorDetail || null,
      isSingleExtractionMode: true, // Mark as single extraction mode
    });
  };

  return (
    <>
      <DivInputWrapper>
        {showFieldCheckboxes && (
          <Checkbox
            data-cy={`mark-field-checkbox-${field}`}
            checked={handleCheckboxChecked()}
            onChange={handleCheckboxOnChange}
            color="primary"
          />
        )}
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <TextField
            sx={{
              flex: 1,
              mr: 1,
              minWidth: 0,
              ...(readOnly && {
                '& .MuiInputBase-input': {
                  color: 'text.disabled',
                  cursor: 'not-allowed',
                },
              }),
            }}
            data-cy={`mark-field-${field}`}
            error={errorMessage.length > 0 || isMutationError}
            autoComplete="off"
            fullWidth
            margin="dense"
            id={field}
            inputProps={{
              'data-cy': `input-mark-field-${field}`,
              readOnly,
              onKeyDown: (event) => {
                if (readOnly) {
                  event.preventDefault();
                  return;
                }
                if (isReview) return;
                event.preventDefault();
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ gap: 0.5 }}>
                  <IconButton
                    onClick={clear}
                    data-cy={`btn-field-clear-${field}`}
                    disabled={disableClear}
                    edge="end"
                    size="small"
                    aria-label="Clear field"
                  >
                    <FormatClear className={MarkFieldStyles.inputIcon} fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={handleSave}
                    data-cy={`btn-field-save-${field}`}
                    disabled={disableSave}
                    edge="end"
                    size="small"
                    aria-label="Save field"
                  >
                    <CheckCircleIcon className={MarkFieldStyles.inputIcon} fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            label={
              // <div className={MarkFieldStyles.inputLabelRoot}>
              <DivInputLabelStyled
                className={`${hasMismatchedTokens ? MarkFieldStyles.inputLabelError : ''} `}
                data-cy={`input-mark-field-v2-${field}`}
                style={{
                  fontWeight: highlightField ? 'bold' : 'normal',

                  color: `${
                    showValidations && fieldHasNonDismissedError
                      ? isRequiredAndValueEmpty(highestPriorityValidationError?.type, value)
                        ? colorMap[whichColorMapObject(accessibility)].missing.color
                        : colorMap[whichColorMapObject(accessibility)].invalid.color
                      : highlightField || focused || isSpreadsheetFocused
                        ? theme.palette.primary.main
                        : 'inherit'
                  }`,
                }}
              >
                <>
                  {showValidations && isValidating ? (
                    <Tooltip placement="top-start" title={VALIDATING_TOOLTIP}>
                      <CircularProgress
                        size={12}
                        thickness={5}
                        sx={{ color: 'text.secondary', verticalAlign: 'middle' }}
                        data-cy={`validating-${field}`}
                      />
                    </Tooltip>
                  ) : showValidations && isValidationNotReady ? (
                    <Tooltip placement="top-start" title={VALIDATION_NOT_READY_TOOLTIP}>
                      <IconButton
                        onClick={retryValidation}
                        className="inputLabelIcon"
                        data-cy={`btn-validation-retry-${field}`}
                        size="small"
                        sx={{ p: 0 }}
                      >
                        <ReplayIcon
                          fontSize="small"
                          sx={{ color: colorMap[whichColorMapObject(accessibility)].missing.color }}
                        />
                      </IconButton>
                    </Tooltip>
                  ) : showValidations && fieldErrorIsDismissed ? (
                    <Tooltip
                      placement="top-start"
                      title={`Error Dismissed${highestPriorityValidationError?.dismissalReason ? ` (${highestPriorityValidationError.dismissalReason})` : ''}`}
                      disableFocusListener
                      disableTouchListener
                      PopperProps={{
                        popperOptions: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, 0],
                              },
                            },
                          ],
                        },
                      }}
                    >
                      <WarningIcon
                        fontSize="small"
                        sx={{
                          color: colorMap[whichColorMapObject(accessibility)].missing.color,
                        }}
                      />
                    </Tooltip>
                  ) : showValidations && fieldHasNonDismissedError ? (
                    <Tooltip
                      placement="top-start"
                      title={allValidationErrorsMessages.join(', ') || ''}
                      disableFocusListener
                      disableTouchListener
                      PopperProps={{
                        popperOptions: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, 0],
                              },
                            },
                          ],
                        },
                      }}
                    >
                      <ErrorIcon
                        fontSize="small"
                        sx={{
                          color: `${
                            showValidations && fieldHasNonDismissedError
                              ? isRequiredAndValueEmpty(highestPriorityValidationError?.type, value)
                                ? colorMap[whichColorMapObject(accessibility)].missing.color
                                : colorMap[whichColorMapObject(accessibility)].invalid.color
                              : 'inherit'
                          }`,
                        }}
                      >
                        {' '}
                      </ErrorIcon>
                    </Tooltip>
                  ) : null}
                  {hasMismatchedTokens ? (
                    <Tooltip
                      placement="top-start"
                      title={errorResponses.mismatchedTokenIndex}
                      disableFocusListener
                      disableTouchListener
                      PopperProps={{
                        popperOptions: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, 0],
                              },
                            },
                          ],
                        },
                      }}
                    >
                      <ErrorIcon fontSize="small" sx={{ color: '#f44336' }}>
                        {' '}
                      </ErrorIcon>
                    </Tooltip>
                  ) : null}
                  {hasMismatchedTokens || fieldHasValidationError ? <> </> : null}
                  <Tooltip
                    placement="top-start"
                    title={showTooltip()}
                    disableFocusListener
                    disableTouchListener
                    PopperProps={{
                      popperOptions: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, 0],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span data-cy={`field-label-${field}`}>{field}</span>{' '}
                      <div style={{ marginLeft: '5px' }}>
                        <FieldValidationIcons
                          validationConfig={validationConfig}
                          hasFixedWidth={false}
                          fieldLabel={field}
                        />
                      </div>
                    </div>
                  </Tooltip>{' '}
                  {featureFlags.reviewEditFieldProperties ? (
                    <Tooltip
                      data-cy={`tooltip-additionalField-${field}`}
                      title={'Edit field properties'}
                      placement="top"
                      arrow
                    >
                      <span>
                        <IconButton
                          onClick={handleModal}
                          className="inputLabelIcon"
                          data-cy="edit-field-properties"
                          size="small"
                        >
                          <EditIcon color={'action'} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                  {isReview && showValidations && fieldHasValidationError ? (
                    <Tooltip
                      data-cy={`tooltip-validation-dismissal-${field}`}
                      title={
                        fieldErrorIsDismissed ? 'Edit dismissal reason' : 'Dismiss validation error'
                      }
                      placement="top"
                      arrow
                    >
                      <span>
                        <IconButton
                          onClick={handleOpenDismissalDialog}
                          className="inputLabelIcon"
                          data-cy="btn-validation-dismissal"
                          size="small"
                        >
                          <RuleIcon color={'action'} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                </>
              </DivInputLabelStyled>
              // </div
            }
            name={field}
            inputRef={ref}
            value={value}
            helperText={errorMessage}
            InputLabelProps={{
              shrink: true,
            }}
            // FIXME: Should this be disabled?
            // disabled={disabled}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onClick={handleClick}
          />
        </div>
      </DivInputWrapper>

      <FieldModal
        dataTypesResponse={dataTypesResponse}
        ebId={ebId}
        tsId={tsId}
        extractionId={extractionId}
        field={field}
        fieldDataType={fieldDataType}
        fieldDataTypeConfig={fieldDataTypeConfig}
        fieldId={fieldId}
        glyntTags={glyntTags}
        initValue={initValue}
        isAdditionalField={isAdditionalField}
        isReview={isReview}
        openModal={openModal}
        setOpenModal={setOpenModal}
        setUpdateFieldListLabel={setUpdateFieldListLabel}
        validationConfig={validationConfig}
      />
    </>
  );
});

export default MarkField;
